import React, { useState, useEffect } from 'react'
import GridLayout, { Layout, Layouts } from 'react-grid-layout'
import { Dialog } from '@e3dc-react/shell/Elements/Dialog'
import { useCustomTranslation } from '@e3dc-react/shell/Hooks/useCustomTranslation'
import { CustomLayout } from '../../shared/utils/chartModel'
import useResizeObserver from 'use-resize-observer'
import { makeStyles } from 'tss-react/mui'
import { Typography } from '@mui/material'
import { getChartName } from '../../../system-charts/shared/utils/SystemChartUtils'
import { useSnackbar } from '@e3dc-react/shell/Hooks/Snackbar'
import { Select } from '@e3dc-react/shell/Elements/Select'
import { LabeledOption } from '../../../../Types/globalTypes'
import { useNavigate } from 'react-router-dom'
import { useTypedSelector } from '../../../../State/RootReducer'
import { CheckboxField } from '@e3dc-react/shell/Elements/InputFields'
import { useUserSettings } from 'src/Hooks'

export interface ChartLayoutEditorProps {
  place: number
  onClose: () => void
  defaultAndCustomCharts: CustomLayout[]
  customLayout: Layouts
}

const useStyles = makeStyles()(theme => ({
  gridItem: {
    background: theme.palette.background.layoutEditorItem,
    padding: theme.spacing(2),
  },
  selectWrapper: {
    display: 'flex',
    justifyContent: 'center',
    padding: theme.spacing(2),
  },
  select: {
    maxWidth: 250,
    marginRight: theme.spacing(2),
  },
  grid: {},
}))

const ChartLayoutEditor: React.FC<ChartLayoutEditorProps> = ({ place, onClose, customLayout, defaultAndCustomCharts }) => {
  const { t } = useCustomTranslation()
  const navigate = useNavigate()
  const { enqueueErrorStack, enqueueSuccessStack } = useSnackbar()
  const installations = useTypedSelector(state => state.storage.installations)
  const activeInstallation = useTypedSelector(state => state.storage.activeInstallation)
  const { ref, width } = useResizeObserver<HTMLDivElement>({ box: 'border-box' })
  const [isSaving, setIsSaving] = useState(false)
  const [currentLayout, setCurrentLayout] = useState(customLayout['lg'])
  const [allLayouts, setAllLayouts] = useState(customLayout)
  const [useForAllInstallations, setUseForAllInstallations] = useState(installations.length > 1 ? false : true)
  const [breakpoint, setBreakpoint] = useState('lg')
  const [cols, setCols] = useState(12)
  const { classes } = useStyles()
  const { updateUserSettings } = useUserSettings()
  const breakpointOptions: LabeledOption<'lg' | 'md' | 'xs'>[] = [
    { label: t('systemDataHandler.layoutEditor.breakpoints.lg'), value: 'lg' },
    { label: t('systemDataHandler.layoutEditor.breakpoints.md'), value: 'md' },
    { label: t('systemDataHandler.layoutEditor.breakpoints.sm'), value: 'xs' },
  ]

  useEffect(() => {
    if (breakpoint === 'lg') {
      setCols(12)
      setCurrentLayout(customLayout['lg'])
    } else if (breakpoint === 'md') {
      setCols(10)
      setCurrentLayout(customLayout['md'])
    } else if (breakpoint === 'xs') {
      setCols(1)
      setCurrentLayout(customLayout['xs'])
    }
  }, [breakpoint, customLayout])

  const onSave = (): void => {
    setIsSaving(true)

    const key = useForAllInstallations ? 'all' : activeInstallation
    const layoutsWithKey = {
      [key as string]: allLayouts,
    }

    updateUserSettings({ chartLayout: [JSON.stringify(layoutsWithKey)] })
      .then(() => {
        setIsSaving(false)
        enqueueSuccessStack(t('systemDataHandler.editor.saveLayoutSuccess'))
        onClose()
        navigate(0)
      })
      .catch(() => {
        setIsSaving(false)
        enqueueErrorStack(t('systemDataHandler.editor.saveLayoutError'))
      })
  }

  const onLayoutChange = (currLayout: Layout[]): void => {
    setCurrentLayout(currLayout)
    setAllLayouts({ ...allLayouts, [breakpoint]: currLayout })
  }

  return (
    <Dialog
      title={t('systemDataHandler.layoutEditor.title')}
      onClose={() => onClose()}
      place={place}
      actions={{
        primary: {
          name: t('common.save'),
          onClick: () => onSave(),
          isLoading: isSaving,
        },
        secondary: [
          {
            name: t('common.cancel'),
          },
        ],
      }}
    >
      <Typography>{t('systemDataHandler.layoutEditor.hint')}</Typography>
      <div className={classes.selectWrapper}>
        <Select
          label={t('systemDataHandler.layoutEditor.breakpoints.title')}
          options={breakpointOptions}
          onChange={setBreakpoint}
          value={breakpoint}
          className={classes.select}
        />
        {installations.length > 1 && (
          <CheckboxField label={t('systemDataHandler.layoutEditor.useForAll')} value={useForAllInstallations} onChange={setUseForAllInstallations} />
        )}
      </div>
      <div ref={ref}>
        <GridLayout
          className={classes.grid}
          layout={currentLayout}
          cols={cols}
          rowHeight={50}
          resizeHandles={['ne', 'se', 'nw', 'se']}
          onLayoutChange={onLayoutChange}
          width={width}
        >
          {defaultAndCustomCharts.map((chart, idx) => {
            return (
              <div key={String(chart.title) + '-' + (chart.id || 'index' + idx)} className={classes.gridItem}>
                <Typography variant={'h3'}>{getChartName(chart, t)}</Typography>
              </div>
            )
          })}
        </GridLayout>
      </div>
    </Dialog>
  )
}

export default ChartLayoutEditor
